import { useTranslator } from '@bluebeam/react-i18n';
import ImportDrawingsModal from 'components/modals/ImportDrawingsModal/ImportDrawingsModal';
import OutsideClick from 'components/OutsideClick';
import { FeatureFlag, useFeatureFlags } from 'featureFlags';
import { useIntegrationsCheck } from 'hooks/useIntegrationsCheck';
import React, { FC, useEffect, useState } from 'react';
import { Manager, Popper, Reference } from 'react-popper';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { ModalState } from 'records/ModalState';
import withAppStore, { WithAppStoreProps } from 'utils/withAppStore';

interface Props extends RouteComponentProps, WithAppStoreProps {
  disabled?: boolean;
}

const ImportDrawingsDropdown: FC<Props> = ({
  disabled,
  actions,
  history,
  appState,
}) => {
  const [open, setOpen] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const { studioProjects } = appState;

  const { translate } = useTranslator(['buttons', 'dropdownOptions']);

  const { [FeatureFlag.ImportFromStudio]: isImportFromStudioEnabled } =
    useFeatureFlags();

  const { areIntegrationsEnabled } = useIntegrationsCheck();

  useEffect(() => {
    actions.resetBreadcrumbs();
    actions.resetStudioView();
    actions.resetIntegrationsView();
    actions.resetIntegrationsSelectedItems();
    actions.setStudioSelectedFiles([]);
    actions.setStudioSelectedFolders([]);
    actions.clearStudioNavigation();
  }, []);

  const toggleDropdown = (): void => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleOutsideClick = (): void => {
    setOpen(false);
  };

  const handleCloseImportModal = (): void => {
    setImportModalOpen(false);
  };

  const handleFromImportsClick = (): void => {
    setImportModalOpen(true);
    setOpen(false);
  };

  const handleFromStudioClick = (): void => {
    const { selectedProject } = appState;

    history.push(`/project/${selectedProject}/drawings/studio`);
  };

  return (
    <>
      {importModalOpen && (
        <ImportDrawingsModal onCancel={handleCloseImportModal} />
      )}
      <Manager>
        <Reference>
          {({ ref }): React.ReactNode => (
            <button
              ref={ref}
              id="import-drawings-btn"
              className="btn-lg btn-outline-primary w100"
              disabled={disabled}
              onClick={toggleDropdown}
            >
              {translate('buttons:importDrawings')}
            </button>
          )}
        </Reference>
        <Popper placement="bottom-end">
          {({ ref, style, placement }): React.ReactNode => {
            return open ? (
              <div
                ref={ref}
                className="bgd-white b-2"
                style={{
                  ...style,
                  zIndex: 2,
                }}
                data-placement={placement}
              >
                <OutsideClick onOutsideClick={handleOutsideClick}>
                  <div
                    className="column items-center color-primary-dark font-size-4"
                    style={{ width: '11.5rem' }}
                  >
                    {areIntegrationsEnabled && (
                      <div
                        data-analytics="import-drawings-from-integrations"
                        className="bb-2 w100 flex-center py1"
                      >
                        <div
                          className="m2 py1 cursor-pointer"
                          onClick={handleFromImportsClick}
                        >
                          {translate('dropdownOptions:fromIntegrations')}
                        </div>
                      </div>
                    )}
                    {isImportFromStudioEnabled && studioProjects.size !== 0 && (
                      <div className="bb-2 w100 flex-center py1">
                        <div
                          className="m2 py1 cursor-pointer"
                          onClick={handleFromStudioClick}
                        >
                          {translate('dropdownOptions:fromStudio')}
                        </div>
                      </div>
                    )}
                  </div>
                </OutsideClick>
              </div>
            ) : null;
          }}
        </Popper>
      </Manager>
    </>
  );
};

export default withRouter(withAppStore<Props>(ImportDrawingsDropdown));
